import type { Tables } from '@biopeak/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase.ts';

export type StoredDocumentAnalysis = Tables<'document_ai_analysis'>;

/**
 * Formato da saída estruturada do modelo para a análise de UM exame.
 *
 * Escrito à mão porque a coluna é `jsonb` e o gerador do Supabase só enxerga
 * `Json` — mesma situação de `AnalysisContent`. O schema real que o modelo é
 * obrigado a seguir vive em `supabase/functions/document-analysis/prompt.ts`;
 * este tipo é a metade da frente e precisa ser mantido junto com aquele.
 */
export interface DocumentAnalysisContent {
  resumo: string;
  achados: {
    titulo: string;
    analitos: string[];
    direcao: 'alterado' | 'melhora' | 'piora' | 'estavel';
    observacao: string;
    evidencia: { numeros: number[]; descricao: string };
    confianca: 'alta' | 'media' | 'baixa';
    /**
     * Escrito pela edge function, não pelo modelo: `false` significa que algum
     * número citado não foi encontrado no brief. A interface MOSTRA isso — um
     * achado não verificado misturado aos verificados seria pior que não ter
     * verificação nenhuma, porque daria confiança falsa ao conjunto.
     */
    verificado?: boolean;
  }[];
  correlacoes: { analitos: string[]; observacao: string }[];
  alertas: { titulo: string; porque: string; severidade: 'alta' | 'media' }[];
  perguntas_sugeridas: string[];
  qualidade_do_dado: string;
}

/** A análise mais recente já gravada para este exame (a RLS filtra). */
export function useLatestDocumentAnalysis(documentId: string) {
  return useQuery({
    queryKey: ['document-analysis', documentId],
    queryFn: async (): Promise<StoredDocumentAnalysis | null> => {
      const { data, error } = await supabase
        .from('document_ai_analysis')
        .select('*')
        .eq('document_id', documentId)
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export interface DocumentAnalysisResult {
  status: 'ok' | 'sem_dado';
  cached?: boolean;
  analysis?: StoredDocumentAnalysis;
  motivo?: 'aguardando_conferencia' | 'sem_analito' | 'documento_inexistente';
  itens_pendentes?: number;
  message?: string;
}

/**
 * Erro com CÓDIGO, não só mensagem — mesma razão de `AnalysisError`: a tela
 * precisa distinguir "o paciente não autorizou" de "a chave não está
 * configurada" de "o modelo falhou", porque exigem ações diferentes de quem
 * está olhando.
 */
export class DocumentAnalysisError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = 'DocumentAnalysisError';
  }
}

export function useGenerateDocumentAnalysis(documentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options?: { force?: boolean }) => {
      const { data, error } = await supabase.functions.invoke<DocumentAnalysisResult>(
        'document-analysis',
        { body: { documentId, force: options?.force ?? false } },
      );

      // `functions.invoke` embrulha respostas não-2xx num FunctionsHttpError e
      // não devolve o corpo. Sem lê-lo, o código de erro que a função tomou o
      // cuidado de mandar se perderia justamente nos casos em que ele importa.
      if (error) {
        const resposta = (error as { context?: Response }).context;
        if (resposta) {
          try {
            const corpo = await resposta.json();
            throw new DocumentAnalysisError(
              corpo?.error ?? 'desconhecido',
              corpo?.message ?? corpo?.error ?? error.message,
            );
          } catch (err) {
            if (err instanceof DocumentAnalysisError) throw err;
          }
        }
        throw new DocumentAnalysisError('desconhecido', error.message);
      }

      return data as DocumentAnalysisResult;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['document-analysis', documentId] });
    },
  });
}
